import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Bell, Loader2 } from "lucide-react";

interface JobAlertButtonProps {
  searchQuery: string;
  filters: {
    location?: string;
    job_type?: string;
    experience_level?: string;
    is_remote?: boolean;
    salary_min?: number;
  };
  jobCount: number;
}

export const JobAlertButton = ({ searchQuery, filters, jobCount }: JobAlertButtonProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [frequency, setFrequency] = useState<'daily' | 'weekly'>('weekly');
  const [isCreating, setIsCreating] = useState(false);

  const handleCreateAlert = async () => {
    if (!user) {
      navigate('/auth');
      return;
    }

    setIsCreating(true);
    
    try {
      // Create job alert
      const { error } = await supabase
        .from('job_alerts')
        .insert({
          user_id: user.id,
          title: searchQuery || 'Job Alert',
          keywords: searchQuery ? [searchQuery] : [],
          locations: filters.location ? [filters.location] : [],
          job_types: filters.job_type ? [filters.job_type] : [],
          experience_levels: filters.experience_level ? [filters.experience_level] : [],
          is_remote: filters.is_remote || false,
          salary_min: filters.salary_min,
          frequency,
          is_active: true
        });

      if (error) throw error;

      toast({
        title: "Alert created",
        description: `You'll receive ${frequency} email alerts for this search.`,
      });

      setIsOpen(false);
    } catch (error) {
      console.error('Error creating job alert:', error);
      toast({
        title: "Error",
        description: "Failed to create job alert. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleButtonClick = () => {
    if (!user) {
      navigate('/auth');
      return;
    }
    setIsOpen(true);
  };

  if (!searchQuery && !filters.location && !filters.job_type) {
    return null; // Don't show for empty searches
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          onClick={handleButtonClick}
          variant="outline"
          className="rounded-full px-4 py-2 text-sm font-medium border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
        >
          <Bell className="w-4 h-4 mr-2" />
          Create alert for this search
        </Button>
      </DialogTrigger>
      
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Job Alert</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          <div className="space-y-2">
            <h4 className="font-medium">Alert Details</h4>
            <div className="bg-muted rounded-lg p-3 space-y-1 text-sm">
              {searchQuery && (
                <div><span className="font-medium">Search:</span> "{searchQuery}"</div>
              )}
              {filters.location && (
                <div><span className="font-medium">Location:</span> {filters.location}</div>
              )}
              {filters.job_type && (
                <div><span className="font-medium">Job Type:</span> {filters.job_type}</div>
              )}
              {filters.experience_level && (
                <div><span className="font-medium">Experience:</span> {filters.experience_level}</div>
              )}
              {filters.is_remote && (
                <div><span className="font-medium">Remote:</span> Yes</div>
              )}
              {filters.salary_min && (
                <div><span className="font-medium">Min Salary:</span> LKR {filters.salary_min.toLocaleString()}</div>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              Found {jobCount} job{jobCount !== 1 ? 's' : ''} matching this search
            </p>
          </div>

          <div className="space-y-3">
            <Label className="text-base font-medium">Email Frequency</Label>
            <RadioGroup value={frequency} onValueChange={(value: 'daily' | 'weekly') => setFrequency(value)}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="daily" id="daily" />
                <Label htmlFor="daily" className="cursor-pointer">
                  Daily - Get notified every day about new jobs
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="weekly" id="weekly" />
                <Label htmlFor="weekly" className="cursor-pointer">
                  Weekly - Get a summary every week (recommended)
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              onClick={() => setIsOpen(false)}
              variant="outline"
              className="flex-1"
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateAlert}
              className="flex-1"
              disabled={isCreating}
            >
              {isCreating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Alert
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};